import { cloneDeep, pick, startCase, trim } from "lodash";

import moment from "moment";
import { CheckIn } from "./checkin";
import { Shipment } from "./shipment";

import {
  AWAITING_PAYMENT,
  BUYER_FIELDS,
  CANCELLED,
  DELIVERED,
  NEW,
  NOT_DELIVERED,
  ON_HOLD,
  PACKING_SLIP,
  READY_TO_PICK,
  READY_TO_SHIP,
  RECEIVED,
  SHIPPED,
  STATUSES_COLOR,
  STATUSES_TEXT,
  TO_DISPATCH,
} from "../../modules/orders/constants/order.constants";
import { getCookedStatus } from "../../modules/orders/utils/order.utils";
import { GoodsIn } from "./goodsIn";

const DEFAULT_SHIPPING_OBJ = {
  accuracy: null,
  address_line: null,
  city: null,
  country: {
    name: "Argentina",
    code: "AR",
  },
  lat: null,
  lon: null,
  post_office_id: null,
  state: "Ciudad Autónoma de Buenos Aires",
  street_name: null,
  street_number: null,
  warehouse_id: null,
  zip_code: null,
};

const DEFAULT_ATTRIBUTES = {
  account: null,
  delivery_for: null,
  external_reference: null,
  external_reference_user: null,
  sale_price: null,
  store_id: null,
  seller_id: null,
  ship_to: DEFAULT_SHIPPING_OBJ,
  ship_to_post_office: {
    id: null,
    external_reference: null,
    external_id: null,
    description: null,
    pickup_point: null,
    drop_off_point: null,
  },
  ship_from: DEFAULT_SHIPPING_OBJ,
  items: [],
  shipping_option: {
    service_type: "ship_pap",
    service_code: null,
  },
  type: "regular",
  ship_to_warehouse_id: null,
  ship_from_warehouse_id: null,
  main_order_id: null,
  main_order: null,
  label_type: null,
  labeled_at: null,
  comment: "",
  dispatch_document_url: null,
  status: NEW,
};

export class Order {
  constructor(attributes) {
    const attrs = {
      ...cloneDeep(DEFAULT_ATTRIBUTES),
      ...attributes,
    };

    Object.keys(attrs).forEach((key) => {
      this[key] = attrs[key];
    });

    if (this.check_in) {
      this.check_in = new CheckIn(this.check_in);
    }

    if (this.shipment) {
      this.shipment = new Shipment(this.shipment);
    }

    if (this.main_order) {
      this.main_order = new Order(this.main_order);
    }

    if (this.goods_in_item) {
      this.goodsIn = new GoodsIn(
        this.goods_in_item.transfer || this.goods_in_item.pickup
      );
    }
  }

  events = null;
  logs = null;
  labels = null;

  //? methods related with the type.
  get isReturn() {
    return this.type === "exchange";
  }

  //? methods related with the order status.
  get isShippedStatus() {
    return this.status === "shipped";
  }

  get isProcessed() {
    return (
      [PACKING_SLIP, READY_TO_PICK, SHIPPED, DELIVERED, NOT_DELIVERED].includes(
        this.status
      ) ||
      (this.status === "on_hold" && !!this.ready_to_ship_at)
    );
  }

  get isNewStatus() {
    return this.status === NEW;
  }

  get isOnHold() {
    return this.status === ON_HOLD;
  }

  get isAwaitingPayment() {
    return this.status === AWAITING_PAYMENT;
  }

  get isReadyToPick() {
    return this.status === READY_TO_PICK;
  }

  get isCanChangeGoodsInByStatus() {
    return (
      this.status === NEW || (this.status === ON_HOLD && !this.ready_to_ship_at)
    );
  }

  get isInFinalStatus() {
    return [CANCELLED, NOT_DELIVERED, DELIVERED].includes(this.status);
  }

  //? methods related with carrier.
  get isShipnowCarrier() {
    return this.shipping_option.carrier_code === "shipnow";
  }

  //? methods related with the visits.
  get lastCompletedVisit() {
    return this.shipment ? this.shipment.lastCompletedVisit : null;
  }

  get lastVisit() {
    return this.shipment ? this.shipment.lastVisit : null;
  }

  get isLastCompletedVisitNotDelivered() {
    return this.shipment
      ? this.shipment.isLastCompletedVisitNotDelivered
      : false;
  }

  get isLastVisitNotDelivered() {
    return this.shipment ? this.shipment.isLastVisitNotDelivered : false;
  }

  get isCustomOperation() {
    return (
      this.shipping_option &&
      this.shipping_option.service_code === "custom_operation"
    );
  }

  get isManagedByShipnow() {
    return !this.isCustomOperation;
  }

  get isMultiPackage() {
    let res = false;

    if (this.items.length > 1) {
      const firstPackageNumber = this.items[0].package_number;

      for (let i = 1; i < this.items.length; i++) {
        if (this.items[i].package_number !== firstPackageNumber) {
          return true;
        }
      }
    }

    return res;
  }

  get isTrackingEnabled() {
    if (!this.uid || !this.isManagedByShipnow) {
      return false;
    }

    if (!this.isReturn) {
      const statuses = [AWAITING_PAYMENT, CANCELLED, NEW, ON_HOLD];

      return !statuses.includes(this.status);
    }
    return true;
  }

  isMyOrder(loggedInUser) {
    if (!loggedInUser) return false;

    if (loggedInUser.isMultiAccount) return true;

    return loggedInUser.warehouses.some(
      (w) =>
        w.id === this.ship_from_warehouse_id ||
        w.id === this.ship_to_warehouse_id
    );
  }

  get isOwnerAccountActive() {
    return !this.account || this.account.active;
  }

  get packageCount() {
    if (!this.items) return 0;

    return new Set(this.items.map((i) => i.package_number)).size;
  }

  get cookedItems() {
    const items = this.items;
    if (!items) return [];

    let _items =
      items.length > 1 ? items.filter((item) => !item.cross_docking) : items;

    return _items.map((i) => {
      i["key"] = `${i.id}-${i.package_number}`;
      return i;
    });
  }

  get itemsQuantity() {
    return (
      this.items &&
      this.items.reduce((accumulator, item) => item.quantity + accumulator, 0)
    );
  }

  get withoutShippingOption() {
    return (
      !this.shipping_option.service_type && !this.shipping_option.service_code
    );
  }

  removeOrderItem(item) {
    let items = this.items;
    if (!items.includes(item)) {
      return this;
    } else {
      this.items = [];
      const newItemList = items.filter(
        (i) => !(i.id === item.id && i.package_number === item.package_number)
      );
      this.items = newItemList;

      this.recalculateWeight();
    }
  }

  addOrderItem(item, quantity) {
    let items = this.items;

    if (items.includes(item)) {
      return this;
    } else {
      const newItem = {
        id: item.id,
        quantity: quantity,
        title: item.title,
        unit_price:
          item.unit_price ||
          (item.price ? item.price.unit_price || item.price.retail : null),
        stock: item.stock,
        thumbnail_url: item.thumbnail_url,
        external_reference_user: item.external_reference_user,
        external_reference: item.external_reference,
        dimensions: {
          weight:
            item.dimensions?.weight || item.declared_dimensions?.weight || 0,
          width: item.dimensions?.width || item.declared_dimensions?.width || 0,
          length:
            item.dimensions?.length || item.declared_dimensions?.length || 0,
          height:
            item.dimensions?.height || item.declared_dimensions?.height || 0,
        },
        package_number: item.package_number,
        variant_weight:
          item.variant_weight ||
          item.dimensions?.weight ||
          item.declared_dimensions?.weight ||
          0,
      };

      items.push(newItem);
      this.items = items;
      this.recalculateWeight();
    }
  }

  get buyerEmail() {
    const buyerInfo = this.buyerInfo;

    if (buyerInfo.email && buyerInfo.email.indexOf("@mail.mercadolibre") > 0) {
      return buyerInfo.email.replace(/\..+@/gi, "@");
    }

    return buyerInfo.email ? buyerInfo.email.toLowerCase() : "";
  }

  get buyerInfo() {
    return this.isReturn ? this.ship_from : this.ship_to;
  }

  get buyerName() {
    const buyerInfo = this.buyerInfo;
    const fullName = buyerInfo?.full_name;

    if (fullName && trim(fullName) === "") {
      return buyerInfo.email &&
        buyerInfo.email.indexOf("@mail.mercadolibre") === -1
        ? buyerInfo.email.substr(0, buyerInfo.email.indexOf("@"))
        : buyerInfo.email.substr(0, buyerInfo.email.indexOf("."));
    }

    return fullName;
  }

  getReturnOrder() {
    const returnOrder = new Order();

    returnOrder.type = "exchange";
    returnOrder.items = cloneDeep(this.items);
    returnOrder.shipping_option = cloneDeep(this.shipping_option);
    returnOrder.shipping_option.service_type = "ship_sap";

    if (this.isToAddress) {
      returnOrder.ship_from = cloneDeep(this.buyerInfo);
    } else {
      returnOrder.ship_from = {
        ...cloneDeep(DEFAULT_SHIPPING_OBJ),
        ...pick(this.buyerInfo, BUYER_FIELDS),
      };
    }

    returnOrder.seller_id = this.seller_id;
    returnOrder.seller_name = this.seller_name;
    returnOrder.pointsOfSale = this.pointsOfSale;
    returnOrder.main_order_id = this.id;
    returnOrder.ship_to_warehouse_id = this.ship_from_warehouse_id;
    returnOrder.external_reference = this.generateReturnExternalReference();

    return returnOrder;
  }

  getChangeOrder() {
    const changeOrder = new Order();
    changeOrder.seller_id = this.seller_id;
    changeOrder.seller_name = this.seller_name;
    changeOrder.main_order_id = this.id;
    changeOrder.external_reference = this.generateSendExchangeReference();
    changeOrder.ship_from_warehouse_id = this.ship_from_warehouse_id;
    changeOrder.items = [];
    if (this.isToAddress) {
      changeOrder.ship_to = cloneDeep(this.buyerInfo);
    } else {
      changeOrder.ship_to = {
        ...cloneDeep(DEFAULT_SHIPPING_OBJ),
        ...pick(this.buyerInfo, BUYER_FIELDS),
      };
    }
    changeOrder.ship_from = cloneDeep(this.ship_from);

    if (this.isPAP) {
      changeOrder.shipping_option = cloneDeep(this.shipping_option);
      changeOrder.shipping_option.service_code = null;
    }

    return changeOrder;
  }

  get withStock() {
    return !this.withoutStock;
  }

  get withoutStock() {
    return this.items.some((i) => i.quantity > i.stock.on_hand);
  }

  setBuyersInfo(buyersInfo) {
    this.ship_to.city = buyersInfo.city;
    this.ship_to.country.name = buyersInfo.country.name;
    this.ship_to.country.code = buyersInfo.country.code;
    this.ship_to.floor = buyersInfo.floor;
    this.ship_to.lat = buyersInfo.lat;
    this.ship_to.lon = buyersInfo.lon;
    this.ship_to.state = buyersInfo.state;
    this.ship_to.street_name = buyersInfo.street_name;
    this.ship_to.street_number = buyersInfo.street_number;
    this.ship_to.unit = buyersInfo.unit;
    this.ship_to.zip_code = buyersInfo.zip_code;
  }

  get isResend() {
    return this.main_order_id && this.type === "resend";
  }

  generateIntDate() {
    const date = new Date();

    return moment(date).format("DDMYYYYHmmss");
  }

  generateReturnExternalReference() {
    return `DEV-${this.generateIntDate()}`;
  }

  generateSendExchangeReference() {
    return `BIS-${this.generateIntDate()}`;
  }

  get totalItemsUnits() {
    return this.items
      .filter((i) => !i.cross_docking)
      .reduce((totalItems, item) => parseInt(item.quantity) + totalItems, 0);
  }

  get hasNuntiusCarrier() {
    return ["bmba", "shipnow"].includes(this.shipping_option.carrier_code);
  }

  get shipFromAddressRegion() {
    if (this.shipping_option.service_code === "dhl_express_worldwide") {
      return `${
        this.ship_from.state && startCase(this.ship_from.state.toLowerCase())
      }, ${
        this.ship_from.country &&
        startCase(this.ship_from.country.name.toLowerCase())
      }`;
    }

    if (this.ship_from.city === this.ship_from.state) {
      return `${startCase(this.ship_from.city?.toLowerCase())}`;
    }

    return `${
      this.ship_from.city && startCase(this.ship_from.city?.toLowerCase())
    }, ${
      this.ship_from.state && startCase(this.ship_from.state?.toLowerCase())
    }`;
  }

  get toAddress() {
    return this.shipping_option.service_code === "pickup_warehouse" &&
      this.ship_to_warehouse &&
      this.ship_to_warehouse.address
      ? this.ship_to_warehouse.address
      : this.ship_to;
  }

  get shipToAddressRegion() {
    if (this.shipping_option.service_code === "dhl_express_worldwide") {
      return `${startCase(this.toAddress.state.toLowerCase())}, ${startCase(
        this.toAddress.country.name.toLowerCase()
      )}`;
    }

    if (this.toAddress.city === this.toAddress.state) {
      return `${
        this.toAddress.city
          ? startCase(this.toAddress.city.toLowerCase())
          : this.toAddress.state
          ? startCase(this.toAddress.state.toLowerCase())
          : ""
      }`;
    }

    return `${
      this.toAddress.city && startCase(this.toAddress.city.toLowerCase())
    }, ${
      this.toAddress.state && startCase(this.toAddress.state.toLowerCase())
    }`;
  }

  source(loggedInUser) {
    const storeName = this.store ? this.store.name : "Pedidos Manuales";

    if (loggedInUser.isMultiAccount) {
      return `${this.seller_name} (${storeName})`;
    }

    return storeName;
  }

  get reference() {
    return this.external_reference_user || this.external_reference || this.id;
  }

  get statusText() {
    return STATUSES_TEXT[getCookedStatus(this)];
  }

  get statusColor() {
    if (!this.isManagedByShipnow || !this.isOwnerAccountActive || this.archived)
      return "grey";

    return STATUSES_COLOR[getCookedStatus(this)];
  }

  get statusChangedAt() {
    switch (this.status) {
      case "new":
      case "awaiting_payment":
        return moment(this.timestamps.created_at);
      case getCookedStatus(this):
        return moment(this.timestamps[`${this.status}_at`]);
      default:
        return this.updated_at;
    }
  }

  get isPending() {
    return (
      this.shipping_option && this.shipping_option.service_code === "pending"
    );
  }

  get getQuantityReceived() {
    return this.goods_in_item?.quantity_received;
  }

  abilities = {
    changeGoodsIn: (loggedInUser) => {
      return (
        !this.isReturn &&
        this.isCanChangeGoodsInByStatus &&
        !this.isCustomOperation &&
        !this.isPending
      );
    },
    products_edited: (loggedInUser) => {
      if (this.isReturn) {
        return [NEW, ON_HOLD].includes(this.status);
      }
      return (
        [NEW, AWAITING_PAYMENT].includes(this.status) ||
        (this.status === ON_HOLD && !this.ready_to_ship_at)
      );
    },
    shipment_edited: (loggedInUser) => {
      if (this.isReturn) {
        return [NEW, ON_HOLD].includes(this.status);
      } else {
        return (
          ![SHIPPED, DELIVERED, NOT_DELIVERED, CANCELLED].includes(
            this.status
          ) &&
          (!this.shipment || !this.inDispatchControl()) &&
          !this.isFlex
        );
      }
    },
    cancelled: (loggedInUser) => {
      if (this.isReturn) {
        return [NEW, ON_HOLD].includes(this.status);
      }

      //? Para easy_wms bloqueamos la posibilidad de cancelar pedidos en estado ready_to_ship
      if (this.ship_from_warehouse?.warehouse_system_code === "easy_wms") {
        return ![
          SHIPPED,
          CANCELLED,
          NOT_DELIVERED,
          DELIVERED,
          READY_TO_SHIP,
        ].includes(this.status);
      }

      return (
        ![SHIPPED, CANCELLED, NOT_DELIVERED, DELIVERED].includes(this.status) &&
        (!this.shipment || !this.inDispatchControl())
      );
    },
    archived: (loggedInUser) => !this.archived,
    marked_as_delivered: (loggedInUser) => {
      //? order is already processed.
      if (this.check_in?.received_at) return false;

      if (!this.isMyOrder(loggedInUser)) return false;

      if (
        this.hasNuntiusCarrier &&
        [READY_TO_SHIP, SHIPPED].includes(this.status) &&
        this.timestamps.ready_to_ship_at
      )
        //? Estos pedidos deberían marcarse como entregados en Nuntius
        return false;

      return [
        ON_HOLD,
        AWAITING_PAYMENT,
        READY_TO_PICK,
        READY_TO_SHIP,
        SHIPPED,
        NOT_DELIVERED,
      ].includes(this.status);
    },
    marked_as_not_delivered: (loggedInUser) => {
      //? el pedido ya está procesado.
      if (this.check_in?.received_at) return false;

      if (!this.isMyOrder(loggedInUser)) return false;

      if (
        this.hasNuntiusCarrier &&
        [READY_TO_SHIP, SHIPPED].includes(this.status) &&
        this.timestamps.ready_to_ship_at
      )
        /* Estos pedidos deberían marcarse como no entregados en Nuntius */
        return false;

      return [SHIPPED, DELIVERED].includes(this.status);
    },
    marked_as_received: (loggedInUser) => {
      return (
        loggedInUser.isWarehouseOwner &&
        !this.hasNuntiusCarrier &&
        [NEW, SHIPPED].includes(this.status)
      );
    },
    marked_as_not_received: (loggedInUser) => {
      return (
        loggedInUser.isWarehouseOwner &&
        !this.hasNuntiusCarrier &&
        [NEW, SHIPPED].includes(this.status)
      );
    },
    marked_as_not_returned: (loggedInUser) => {
      if (!loggedInUser.isWhsOwne) return false;

      if (this.account.picking_preference_own === "warehouse") {
        return [TO_DISPATCH, RECEIVED].includes(this.check_in?.status);
      }

      return [RECEIVED].includes(this.check_in?.status);
    },
    marked_as_returned: (loggedInUser) => {
      if (!loggedInUser.isWarehouseOwner) return false;

      if (this.account.picking_preference_own === "warehouse") {
        return [TO_DISPATCH, RECEIVED].includes(this.check_in?.status);
      }

      //? crossdocking accounts with old return group.
      return this.check_in?.status === RECEIVED || this.hadReturnGroup;
    },
    marked_as_back_in_stock: (loggedInUser) => {
      if (!loggedInUser.isWarehouseOwner) return false;

      if (this.account.picking_preference_own === "warehouse") {
        return [TO_DISPATCH, RECEIVED].includes(this.check_in?.status);
      }

      return false;
    },
    marked_as_not_back_in_stock: (loggedInUser) => {
      if (!loggedInUser.isWarehouseOwner) return false;

      if (this.account.picking_preference_own === "warehouse") {
        return [TO_DISPATCH, RECEIVED].includes(this.check_in?.status);
      }

      return false;
    },
    paused: (loggedInUser) => {
      if (this.isReturn) {
        return false;
      } else if (this.isMyOrder(loggedInUser)) {
        return (
          [NEW, AWAITING_PAYMENT, READY_TO_PICK, READY_TO_SHIP].includes(
            this.status
          ) &&
          (!this.shipment || !this.inDispatchControl())
        );
      } else {
        return (
          [NEW, AWAITING_PAYMENT, READY_TO_PICK].includes(this.status) &&
          (!this.shipment || !this.inDispatchControl())
        );
      }
    },
    print_labels: () => {
      if (this.isReturn) {
        return (
          (this.shipment || this.goodsIn) &&
          this.isToWarehouse &&
          this.status === NEW
        );
      }

      return this.shipment || this.goodsIn;
    },
    print_processed_label: (loggedInUser) => {
      return loggedInUser.isAdmin && this.check_in?.isProcessed;
    },
    resumed: (loggedInUser) =>
      this.status === ON_HOLD && (!this.exchangeAndReturn || !this.exchange),
    returned: (loggedInUser) =>
      ["shipped", "delivered"].includes(this.status) && !this.isReturn,
    documents_printed: (loggedInUser) =>
      this.shipping_option &&
      this.shipping_option.service_code !== "shipnow_exchange" &&
      this.shipping_option.service_code !== "exchange_warehouse" &&
      (this.shipping_option.service_mode === "exchange" ||
        this.exchangeAndReturn ||
        (this.shipping_option.service_code === "dhl_express_worldwide" &&
          this.documentUrl !== "")),
    dispatch_document_generated: (loggedInUser) =>
      !this.isExchange &&
      this.isManagedByShipnow &&
      ((this.status === NEW && !this.isPAP) ||
        (this.isToAddress && this.status === READY_TO_SHIP)),
  };

  get documentUrl() {
    if (!this.shipment) return "";

    return this.shipment.document_url || "";
  }

  get labelUrl() {
    if (!this.shipment) return "";

    return this.shipment.label_url || "";
  }

  get serviceType() {
    if (!this.shipping_option || !this.shipping_option.service_type)
      return undefined;

    return this.shipping_option.service_type;
  }

  get isToAddress() {
    return this.isPAP || (this.isSAP && !this.isReturn);
  }

  get isPAP() {
    return this.shipping_option.service_type === "ship_pap";
  }

  get isPAS() {
    return this.shipping_option.service_type === "ship_pas";
  }

  get isSAP() {
    return this.shipping_option.service_type === "ship_sap";
  }

  get hasDispatch() {
    return (
      this.shipment &&
      this.shipment.items.length > 0 &&
      this.shipment.items[0].dispatch_id
    );
  }

  get isToWarehouse() {
    return (
      this.serviceType === "pickup" ||
      (this.ship_from_warehouse_id &&
        this.ship_to_warehouse_id &&
        !this.isReturn) ||
      (this.serviceCode === "exchange_warehouse" &&
        this.serviceType === "exchange")
    );
  }

  get serviceCode() {
    if (!this.shipping_option || !this.shipping_option.service_code)
      return undefined;

    return this.shipping_option.service_code;
  }

  get instanceKey() {
    return this.id
      ? `${this.id}-${new Date(this.updated_at).getTime()}`
      : `new-${new Date()}`;
  }

  get beforePackingStatus() {
    return this.isNewStatus || this.isAwaitingPayment || this.isReadyToPick;
  }

  get exchangeAndReturn() {
    return this.shipping_option.service_mode === "exchange_and_return";
  }

  get isToPostOffice() {
    return this.isPAS || (this.isSAP && this.isReturn);
  }

  get isCrossDockable() {
    if (!this.items) return false;

    const CDItem = this.items.find(
      (i) => i.picking_preference === "cross_docking"
    );

    return !!CDItem;
  }

  get onHoldByRelatedOrder() {
    return (
      !this.exchange &&
      !this.last_status &&
      (this.change_order_id || this.isResend)
    );
  }

  inDispatchControl() {
    return (
      this.shipment &&
      this.shipment.items.some((obj) => {
        return obj.hasOwnProperty("dispatch_id") && obj.dispatch_id !== null;
      })
    );
  }

  canBe(action, loggedInUser) {
    if (this.abilities.hasOwnProperty(action)) {
      return this.abilities[action](loggedInUser);
    }
    return true;
  }

  get isFlex() {
    return this.shipping_option.service_code === "shipnow_flex";
  }

  get isDelayed() {
    return (
      this.shipment &&
      this.status === SHIPPED &&
      this.shipment.condition === "delayed"
    );
  }

  get shipToAddress() {
    let address;

    const addressType = this.isReturn ? this.ship_from : this.toAddress;

    if (
      !addressType.address_line &&
      !addressType.street_name &&
      !addressType.street_number
    )
      return null;

    if (this.isReturn) {
      address = this.ship_from.address_line
        ? `${this.ship_from.address_line}, ${this.shipFromAddressRegion}`
        : `${this.ship_from.street_name}, ${this.ship_from.street_number}, ${this.shipFromAddressRegion}`;

      if (this.ship_from.country.code !== "AR") {
        address = `${address}, ${this.ship_from.country.name}`;
      }
    } else {
      address = this.toAddress.address_line
        ? `${this.toAddress.address_line}, ${this.shipToAddressRegion}`
        : `${this.toAddress.street_name}, ${this.toAddress.street_number}, ${this.shipToAddressRegion}`;

      if (this.toAddress.country && this.toAddress.country.code !== "AR") {
        address = `${address}, ${this.toAddress.country.name}`;
      }
    }

    return address;
  }

  deliverySummary(shippingServices, carriers) {
    if (!this.shipping_option) return "";

    const shipPreffix = this.exchangeAndReturn
      ? "Cambio"
      : this.isReturn
      ? "Devolución"
      : "Envío";

    const shipment_types = {
      ship_pap: `${shipPreffix} a domicilio`,
      ship_pas: "Retira en sucursal",
      ship_sap: `${shipPreffix} desde sucursal`,
      ship_sas: "Retira en sucursal",
      pickup: "Retira en sucursal",
      exchange: "Devolución desde",
    };

    const shipment_codes = {
      pickup_store: "Retiro por depósito con envío",
      custom_service: "Envío por correo personal",
      pending: "Envío pendiente de definir",
      to_be_agreed: "Envío a arreglar con el comprador",
      custom_operation: "Retiro por local",
      pickup_warehouse:
        this.ship_to_warehouse?.account_id === 1
          ? "Retiro por shipnow"
          : "Retiro por depósito",
      dhl_express_worldwide: "Envío al exterior",
      exchange_warehouse: "Devolución a depósito",
    };

    let result =
      shipment_codes[this.shipping_option.service_code] ||
      shipment_types[this.shipping_option.service_type];

    if (
      "exchange" === this.shipping_option.service_type &&
      this.shipping_option.service_code !== "exchange_warehouse"
    ) {
      result = `${result} ${this.shipFromAddressRegion}`;
    }

    if (["ship_pas", "ship_sas"].includes(this.shipping_option.service_type)) {
      const carrier = this.carrier(shippingServices, carriers);
      result = `${result} ${carrier && carrier.name}`;
    }

    return result;
  }

  carrier(shippingServices, carriers) {
    if (this.shipping_option.service_code && shippingServices) {
      const services = shippingServices;

      const service = services.find(
        (c) => c.code === this.shipping_option.service_code
      );

      return service ? service.carrier : null;
    }

    return carriers.find((c) => c.code === this.shipping_option.carrier_code);
  }

  get isGoodsInItem() {
    return false;
  }

  get isSameDay() {
    return (
      this.shipping_option.category === "super_express" &&
      this.shipping_option.service_type === "ship_pap" &&
      this.shipping_option.service_mode === "delivery" &&
      this.shipping_option.service_code !== "shipnow_flex"
    );
  }

  clone() {
    let clone = new Order(cloneDeep(this));

    return clone;
  }

  get hasLabel() {
    return !this.isReturn && this.isManagedByShipnow;
  }

  get getWarnings() {
    let result = [];
    const warnings = cloneDeep(this.warnings);
    if (warnings) {
      Object.keys(warnings).forEach(
        (key) => (result = result.concat(warnings[key]))
      );
    }

    let allowedWarings = [
      "service_pending",
      "from_zip_code_non_existent",
      "to_zip_code_non_existent",
      "drop_off_invalid",
      "pickup_point_invalid",
    ];

    if (this.goodsIn === null) allowedWarings.push("without_stock");

    result = result.filter((value) => -1 !== allowedWarings.indexOf(value));

    return result;
  }

  get getWeight() {
    if (this.weight > 0) return this.weight;

    return this.recalculateWeight();
  }

  recalculateWeight() {
    let result = 0;

    this.items.forEach((item) => {
      result += item.variant_weight * item.quantity;
    });

    return result;
  }

  get isCheckInFinalStatus() {
    return ["cancelled", "completed"].includes(this.check_in.status);
  }

  get haveReturnGroup() {
    if (this.isReturn) {
      return (
        this.account?.picking_preference_own === "cross_docking" &&
        this.check_in?.isProcessed &&
        !!this.check_in.action_resource_id
      );
    }

    return (
      this.account?.picking_preference_own === "cross_docking" &&
      this.check_in?.actionReturn &&
      this.check_in.isProcessed &&
      this.check_in.action_resource_id &&
      this.status === "not_delivered"
    );
  }

  get hadReturnGroup() {
    return (
      this.check_in &&
      this.check_in.scheduled_action === "return" &&
      this.check_in.isProcessed &&
      !this.check_in.action_resource_id &&
      !this.isCheckInFinalStatus
    );
  }

  get showReturnGroup() {
    return this.haveReturnGroup || this.hadReturnGroup;
  }

  get hasItemsWithExpirationDate() {
    return this.items.find((i) => i.expiration_date);
  }

  get hasItemsWithBatch() {
    return this.items.find((i) => i.batch_number);
  }
}
